import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { meetingsApi } from '@/services/meetings';
import { notesApi } from '@/services/notes';
import { Meeting, NoteDetail } from '@/types';
import { t } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import toast from 'react-hot-toast';
import {
  Loader2,
  Calendar,
  MapPin,
  FileText,
  Mic,
  Image,
  ClipboardList,
  Edit,
  Trash2,
  ArrowLeft,
  Save,
  X,
  Plus,
  User,
  RefreshCw,
} from 'lucide-react';
import { mediaUrl } from '../utils/mediaUrl';

const MeetingDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [meeting, setMeeting] = useState<(Meeting & { notes: NoteDetail[] }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<number | null>(null);
  const [editingNote, setEditingNote] = useState<NoteDetail | null>(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [retryingNoteId, setRetryingNoteId] = useState<number | null>(null);

  // Edit form state
  const [editForm, setEditForm] = useState({
    person_name: '',
    company_name: '',
    meeting_date: '',
    meeting_time: '',
    location: '',
  });

  useEffect(() => {
    if (id) {
      fetchMeeting();
    }
  }, [id, location.state]);

  const fetchMeeting = async () => {
    try {
      setLoading(true);
      const response = await meetingsApi.getMeetingById(parseInt(id!));
      setMeeting(response.meeting);
      setEditForm({
        person_name: response.meeting.person_name || '',
        company_name: response.meeting.company_name || '',
        meeting_date: response.meeting.meeting_date || '',
        meeting_time: response.meeting.meeting_time || '',
        location: response.meeting.location || '',
      });
    } catch (error: any) {
      toast.error(t('meeting.failedToLoadMeeting'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!meeting) return;

    try {
      setIsSaving(true);
      await meetingsApi.updateMeeting(meeting.meeting_id, {
        person_name: editForm.person_name,
        company_name: editForm.company_name,
        meeting_date: editForm.meeting_date,
        meeting_time: editForm.meeting_time,
        location: editForm.location,
      });
      toast.success(t('meeting.meetingUpdated'));
      setIsEditing(false);
      await fetchMeeting();
    } catch (error: any) {
      toast.error(t('meeting.failedToUpdateMeeting'));
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteMeeting = async () => {
    if (!meeting) return;

    try {
      await meetingsApi.deleteMeeting(meeting.meeting_id);
      toast.success(t('meeting.meetingDeleted'));
      navigate('/history');
    } catch (error: any) {
      toast.error(t('meeting.failedToDeleteMeeting'));
      console.error(error);
    }
  };

  const handleDeleteNote = async () => {
    if (!deletingNoteId) return;
    try {
      await notesApi.deleteNote(deletingNoteId);
      toast.success(t('notes.noteDeleted'));
      setDeletingNoteId(null);
      await fetchMeeting();
    } catch (error: any) {
      toast.error(t('notes.failedToDeleteNote'));
      console.error(error);
    }
  };

  const handleRetryTranscription = async (noteId: number) => {
    try {
      setRetryingNoteId(noteId);
      toast.success('Rozpoczęto ponowną transkrypcję...');
      await notesApi.retryTranscription(noteId);
      toast.success('Transkrypcja zakończona pomyślnie!');
      await fetchMeeting();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Nie udało się ponowić transkrypcji');
      console.error(error);
    } finally {
      setRetryingNoteId(null);
    }
  };

  const [editingBusinessCard, setEditingBusinessCard] = useState<any>(null);

  const handleEditNote = (note: NoteDetail) => {
    setEditingNote(note);
    // Set content based on note type
    if (note.note_type === 'text') {
      setEditNoteContent(note.content || '');
    } else if (note.note_type === 'voice') {
      setEditNoteContent(note.transcription || '');
    } else if (note.note_type === 'business_card' && note.business_card) {
      setEditingBusinessCard({
        companyName: note.business_card.company || '',
        personName: note.business_card.full_name || '',
        position: note.business_card.position || '',
        phoneMobile: note.business_card.phoneMobile || '',
        phoneLandline: note.business_card.phoneLandline || '',
        email: note.business_card.email || '',
        street: note.business_card.street || '',
        city: note.business_card.city || '',
        postalCode: note.business_card.postalCode || '',
        country: note.business_card.country || '',
        website: note.business_card.website || '',
      });
    }
  };

  const handleSaveNoteEdit = async () => {
    if (!editingNote) return;

    try {
      setIsSaving(true);
      let updateData: any = {};

      if (editingNote.note_type === 'text') {
        updateData = { content: editNoteContent };
      } else if (editingNote.note_type === 'voice') {
        updateData = { transcription: editNoteContent };
      } else if (editingNote.note_type === 'business_card') {
        updateData = { businessCardData: editingBusinessCard };
      }

      await notesApi.updateNote(editingNote.note_id, updateData);
      toast.success(t('notes.noteUpdated'));
      setEditingNote(null);
      setEditNoteContent('');
      setEditingBusinessCard(null);
      await fetchMeeting();
    } catch (error: any) {
      toast.error(t('notes.failedToUpdateNote'));
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5); // HH:MM
  };

  const getNoteIcon = (noteType: string) => {
    if (!noteType) return <FileText className="h-5 w-5" />;
    switch (noteType.toLowerCase()) {
      case 'business_card':
        return <Image className="h-5 w-5" />;
      case 'voice':
        return <Mic className="h-5 w-5" />;
      case 'text':
        return <FileText className="h-5 w-5" />;
      case 'handwritten':
        return <ClipboardList className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  const getNoteTypeLabel = (noteType: string) => {
    if (!noteType) return t('notes.unknown');
    switch (noteType.toLowerCase()) {
      case 'business_card':
        return t('notes.businessCard');
      case 'voice':
        return t('notes.voiceNote');
      case 'text':
        return t('notes.textNote');
      case 'handwritten':
        return t('notes.handwritten');
      default:
        return noteType;
    }
  };

  const getStatusBadge = (status: string) => {
    if (!status) return <Badge variant="outline">{t('notes.unknown')}</Badge>;
    switch (status.toLowerCase()) {
      case 'completed':
        return <Badge variant="default">{t('notes.completed')}</Badge>;
      case 'processing':
        return <Badge variant="secondary">{t('notes.processing')}</Badge>;
      case 'pending':
        return <Badge variant="outline">{t('notes.pending')}</Badge>;
      case 'failed':
        return <Badge variant="destructive">{t('notes.failed')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const renderNoteContent = (note: NoteDetail) => {
    switch (note.note_type.toLowerCase()) {
      case 'business_card':
        return (
          <div className="space-y-4">
            {note.file_path && (
              <div className="border rounded-lg overflow-hidden bg-muted/30">
                <img
                  src={mediaUrl(note.file_path)}
                  alt="Business card scan"
                  className="w-full max-w-md mx-auto"
                />
              </div>
            )}
            {note.business_card ? (
              <div className="space-y-2 text-sm">
                <p><strong>Imię i nazwisko:</strong> {note.business_card.full_name || 'N/A'}</p>
                <p><strong>Firma:</strong> {note.business_card.company || 'N/A'}</p>
                <p><strong>Stanowisko:</strong> {note.business_card.position || 'N/A'}</p>
                <p><strong>Email:</strong> {note.business_card.email || 'N/A'}</p>
                <p><strong>Tel. komórkowy:</strong> {note.business_card.phoneMobile || 'N/A'}</p>
                <p><strong>Tel. stacjonarny:</strong> {note.business_card.phoneLandline || 'N/A'}</p>
                <p><strong>Ulica:</strong> {note.business_card.street || 'N/A'}</p>
                <p><strong>Miasto:</strong> {note.business_card.city || 'N/A'}</p>
                <p><strong>Kod pocztowy:</strong> {note.business_card.postalCode || 'N/A'}</p>
                <p><strong>Kraj:</strong> {note.business_card.country || 'N/A'}</p>
                <p><strong>Strona www:</strong> {note.business_card.website || 'N/A'}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Brak danych</p>
            )}
          </div>
        );

      case 'text':
        return (
          <div className="space-y-2">
            <p className="text-sm whitespace-pre-wrap">{note.content || 'No content'}</p>
          </div>
        );

      case 'voice':
        return (
          <div className="space-y-2">
            {note.file_path && (
              <div>
                <p className="text-sm font-medium mb-1">Audio recording:</p>
                <audio src={mediaUrl(note.file_path)} controls className="w-full" />
              </div>
            )}
            {note.transcription && (
              <div>
                <p className="text-sm font-medium mb-1">Transcription:</p>
                <p className="text-sm whitespace-pre-wrap">{note.transcription}</p>
              </div>
            )}
          </div>
        );

      case 'handwritten':
        return (
          <div className="space-y-2">
            {note.file_path && (
              <div>
                <p className="text-sm font-medium mb-1">Image file:</p>
                <p className="text-sm text-muted-foreground">{note.file_path}</p>
              </div>
            )}
          </div>
        );

      default:
        return <p className="text-sm text-muted-foreground">No content available</p>;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen space-y-4">
        <p className="text-muted-foreground">Meeting not found</p>
        <Link to="/history">
          <Button>Back to History</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="space-y-1">
          <Link to="/history">
            <Button variant="ghost" size="sm" className="mb-2">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('meeting.backToHistory')}
            </Button>
          </Link>
          <h1 className="text-3xl font-bold text-foreground">{t('meeting.meetingDetails')}</h1>
          <p className="text-muted-foreground">
            {t('meeting.createdOn')} {new Date(meeting.created_at).toLocaleDateString('pl-PL')}
          </p>
        </div>
        <div className="flex gap-2">
          {!isEditing ? (
            <>
              <Button onClick={() => setIsEditing(true)} variant="default" size="sm">
                <Edit className="h-4 w-4 mr-2" />
                {t('common.edit')}
              </Button>
              <Button onClick={() => setDeleteDialogOpen(true)} variant="destructive" size="sm">
                <Trash2 className="h-4 w-4 mr-2" />
                {t('common.delete')}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {t('common.save')}
              </Button>
              <Button
                onClick={() => {
                  setIsEditing(false);
                  setEditForm({
                    person_name: meeting.person_name || '',
                    company_name: meeting.company_name || '',
                    meeting_date: meeting.meeting_date || '',
                    meeting_time: meeting.meeting_time || '',
                    location: meeting.location || '',
                  });
                }}
                variant="outline"
                disabled={isSaving}
              >
                <X className="h-4 w-4 mr-2" />
                {t('common.cancel')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Meeting Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('meeting.meetingInformation')}</CardTitle>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 2xl:gap-5">
                <div className="space-y-2">
                  <Label htmlFor="person_name">Imię i nazwisko</Label>
                  <Input
                    id="person_name"
                    type="text"
                    value={editForm.person_name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, person_name: e.target.value })
                    }
                    placeholder="np. Jan Kowalski"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_name">Nazwa firmy</Label>
                  <Input
                    id="company_name"
                    type="text"
                    value={editForm.company_name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, company_name: e.target.value })
                    }
                    placeholder="np. Firma ABC Sp. z o.o."
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 2xl:gap-5">
                <div className="space-y-2">
                  <Label htmlFor="meeting_date">Date</Label>
                  <Input
                    id="meeting_date"
                    type="date"
                    value={editForm.meeting_date}
                    onChange={(e) =>
                      setEditForm({ ...editForm, meeting_date: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meeting_time">Time</Label>
                  <Input
                    id="meeting_time"
                    type="time"
                    value={editForm.meeting_time}
                    onChange={(e) =>
                      setEditForm({ ...editForm, meeting_time: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  type="text"
                  value={editForm.location}
                  onChange={(e) =>
                    setEditForm({ ...editForm, location: e.target.value })
                  }
                  placeholder="Enter meeting location"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {(meeting.person_name || meeting.company_name) && (
                <div className="pb-4 border-b">
                  {meeting.person_name && (
                    <div className="text-lg font-semibold">{meeting.person_name}</div>
                  )}
                  {meeting.company_name && (
                    <div className="text-muted-foreground">{meeting.company_name}</div>
                  )}
                </div>
              )}
              {meeting.user && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Dodane przez:</span>
                  <span className="font-medium">{meeting.user.fullName}</span>
                  <span className="text-muted-foreground">({meeting.user.email})</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-lg">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">{formatDate(meeting.meeting_date)}</span>
                <span className="text-muted-foreground">at</span>
                <span className="font-medium">{formatTime(meeting.meeting_time)}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-muted-foreground" />
                <span>{meeting.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <span>
                  {meeting.notes?.length || 0} note{meeting.notes?.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">{t('meeting.notes')}</h2>
          <div className="flex items-center gap-2">
            <Badge variant="outline">
              {meeting.notes?.length || 0} {t('common.total')}
            </Badge>
            <Button onClick={() => navigate(`/meeting/${meeting.meeting_id}/add-note`)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('meeting.addNote')}
            </Button>
          </div>
        </div>

        {!meeting.notes || meeting.notes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">{t('notes.noNotesForMeeting')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {meeting.notes.map((note) => (
              <Card key={note.note_id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      {getNoteIcon(note.note_type)}
                      <div>
                        <CardTitle className="text-lg">
                          {getNoteTypeLabel(note.note_type)}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          Created {new Date(note.created_at).toLocaleString('pl-PL')}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(note.processing_status)}
                      {note.note_type === 'voice' && note.processing_status === 'failed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRetryTranscription(note.note_id)}
                          disabled={retryingNoteId === note.note_id}
                        >
                          {retryingNoteId === note.note_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                          <span className="ml-2">Ponów</span>
                        </Button>
                      )}
                      {(note.note_type === 'text' || note.note_type === 'voice' || note.note_type === 'business_card') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditNote(note)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeletingNoteId(note.note_id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {renderNoteContent(note)}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Note Dialog */}
      <Dialog open={editingNote !== null} onOpenChange={() => { setEditingNote(null); setEditNoteContent(''); setEditingBusinessCard(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edytuj notatkę</DialogTitle>
            <DialogDescription>
              {editingNote?.note_type === 'text'
                ? 'Edytuj treść notatki tekstowej'
                : editingNote?.note_type === 'voice'
                ? 'Edytuj transkrypcję głosową'
                : 'Edytuj dane wizytówki'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {editingNote?.note_type === 'business_card' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4 2xl:gap-5">
                <div className="space-y-2">
                  <Label htmlFor="edit-personName">Imię i nazwisko</Label>
                  <Input
                    id="edit-personName"
                    value={editingBusinessCard?.personName || ''}
                    onChange={(e) => setEditingBusinessCard({...editingBusinessCard, personName: e.target.value})}
                    placeholder="Jan Kowalski"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-companyName">Firma</Label>
                  <Input
                    id="edit-companyName"
                    value={editingBusinessCard?.companyName || ''}
                    onChange={(e) => setEditingBusinessCard({...editingBusinessCard, companyName: e.target.value})}
                    placeholder="Nazwa firmy"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-position">Stanowisko</Label>
                  <Input
                    id="edit-position"
                    value={editingBusinessCard?.position || ''}
                    onChange={(e) => setEditingBusinessCard({...editingBusinessCard, position: e.target.value})}
                    placeholder="Dyrektor"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editingBusinessCard?.email || ''}
                    onChange={(e) => setEditingBusinessCard({...editingBusinessCard, email: e.target.value})}
                    placeholder="email@firma.pl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-phoneMobile">Tel. komórkowy</Label>
                  <Input
                    id="edit-phoneMobile"
                    value={editingBusinessCard?.phoneMobile || ''}
                    onChange={(e) => setEditingBusinessCard({...editingBusinessCard, phoneMobile: e.target.value})}
                    placeholder="+48 600 000 000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-phoneLandline">Tel. stacjonarny</Label>
                  <Input
                    id="edit-phoneLandline"
                    value={editingBusinessCard?.phoneLandline || ''}
                    onChange={(e) => setEditingBusinessCard({...editingBusinessCard, phoneLandline: e.target.value})}
                    placeholder="+48 22 000 00 00"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="edit-street">Ulica</Label>
                  <Input
                    id="edit-street"
                    value={editingBusinessCard?.street || ''}
                    onChange={(e) => setEditingBusinessCard({...editingBusinessCard, street: e.target.value})}
                    placeholder="ul. Główna 123"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-city">Miasto</Label>
                  <Input
                    id="edit-city"
                    value={editingBusinessCard?.city || ''}
                    onChange={(e) => setEditingBusinessCard({...editingBusinessCard, city: e.target.value})}
                    placeholder="Warszawa"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-postalCode">Kod pocztowy</Label>
                  <Input
                    id="edit-postalCode"
                    value={editingBusinessCard?.postalCode || ''}
                    onChange={(e) => setEditingBusinessCard({...editingBusinessCard, postalCode: e.target.value})}
                    placeholder="00-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-country">Kraj</Label>
                  <Input
                    id="edit-country"
                    value={editingBusinessCard?.country || ''}
                    onChange={(e) => setEditingBusinessCard({...editingBusinessCard, country: e.target.value})}
                    placeholder="Polska"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-website">Strona www</Label>
                  <Input
                    id="edit-website"
                    type="url"
                    value={editingBusinessCard?.website || ''}
                    onChange={(e) => setEditingBusinessCard({...editingBusinessCard, website: e.target.value})}
                    placeholder="www.firma.pl"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {editingNote?.note_type === 'voice' && editingNote?.file_path && (
                  <div className="space-y-2">
                    <Label>Nagranie audio</Label>
                    <audio src={mediaUrl(editingNote.file_path)} controls className="w-full" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="edit-note-content">
                    {editingNote?.note_type === 'text' ? 'Treść notatki' : 'Transkrypcja'}
                  </Label>
                  <Textarea
                    id="edit-note-content"
                    value={editNoteContent}
                    onChange={(e) => setEditNoteContent(e.target.value)}
                    rows={10}
                    className="resize-none"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setEditingNote(null); setEditNoteContent(''); setEditingBusinessCard(null); }}
              disabled={isSaving}
            >
              Anuluj
            </Button>
            <Button
              onClick={handleSaveNoteEdit}
              disabled={isSaving || (editingNote?.note_type !== 'business_card' && !editNoteContent.trim())}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Zapisywanie...
                </>
              ) : (
                'Zapisz zmiany'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Meeting Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('meeting.deleteMeeting')}</DialogTitle>
            <DialogDescription>
              {t('meeting.deleteMeetingConfirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                handleDeleteMeeting();
                setDeleteDialogOpen(false);
              }}
            >
              {t('meeting.deleteMeeting')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Note Confirmation Dialog */}
      <Dialog open={deletingNoteId !== null} onOpenChange={() => setDeletingNoteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('meeting.deleteNote')}</DialogTitle>
            <DialogDescription>
              {t('notes.deleteNoteConfirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingNoteId(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteNote}
            >
              {t('meeting.deleteNote')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeetingDetailPage;
